import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import CustomTable from "../../../components/custom-table/custom-table";
import { useTablesContext } from "../../../context/tablesContextProvider";
import { PaginationKeys } from "../../../enums/nycbsPharmaOrder";

import { openModal } from "risa-oasis-ui_v2";
import CmmOrderDeleteModal from "../../../components/modals/cmmOrderDeleteModal/cmmOrderDeleteModal";
import { FilterSection, FilterValues } from "../../../data-model/filterValues";
import { CmmOrdersTableDataModel } from "../../../data-model/tablesData";
import { CmmFilterKeys } from "../../../enums/cmmFilterKeys";
import { resetSingleOrderData } from "../../../redux/slice/cmm/cmmSingleOrderSlice";
import { fetchUniqueDrugname } from "../../../redux/slice/cmm/uniqueDrugnameSlice";
import { fetchUniqueFormName } from "../../../redux/slice/cmm/uniqueFormNameSlice";
import { setFilter } from "../../../redux/slice/filterValuesSlice";
import {
  appendUniqueNycbsOrderesFromApi,
  appendUniqueNycbsSearchedOrderesFromApi,
  fetchUniqueNycbsOrderesFromApi,
  searchNycbsPharmaOrdersFromApi,
} from "../../../redux/slice/nycbsPharmaExternal/nycbsPharmaOrder";
import { AppDispatch, RootState } from "../../../redux/store/store";
import {
  CmmOrderTableHeader,
  generateTableDataForCmmOrdersTable,
} from "../table/cmmOrderTableData";
import {
  createFilterForFormPickedVia,
  createFilterForInsuranceCardDate,
  createFilterForStatus,
} from "../utils/createFilterDataForForm";
const CmmOrderTable = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { getTableDataFromContext, setTableDataForContext } =
    useTablesContext();
  const tableAttributes = getTableDataFromContext("allCmmOrders");

  const {
    data: ordersData,
    loading,
    pageData,
    showInLineLoader,
  } = useSelector((state: RootState) => state.nycbsPharmaOrders);
  const { filters } = useSelector((state: RootState) => state.filterValues);

  const { data: uniqueDrugnames } = useSelector(
    (state: RootState) => state.uniqueDrugname,
  );
  const { uniqueFormNames } = useSelector(
    (state: RootState) => state.uniqueFormName,
  );

  const [allCmmOrdersTableData, setAllCmmOrdersTableData] = useState<any[]>([]);
  const [initialSearch, setInitialSearch] = useState<boolean>(true);
  const [searchText, setSearchText] = useState("");
  const [batchNumberRetreived, setBatchNumberRetreived] = useState<number[]>(
    [],
  );
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [filterData, setFilterData] = useState<FilterSection[]>([]);
  const [data, setData] = useState<CmmOrdersTableDataModel | null>(null);
  const [filterForTable, setFilterForTable] = useState<FilterValues | null>(
    null,
  );
  const [filterState, setFilterState] = useState<boolean>(false);
  const unsubscribeRef = useRef<Promise<() => void>[]>([]);
  const [endIndex, setEndIndex] = useState<number>(0);
  const initialLimit = 120;

  const finalLimitToUse = useMemo(() => {
    const dataFromContext = tableAttributes.lastBatchFetched
      ? tableAttributes.lastBatchFetched
      : 0;
    const limitToFetch =
      dataFromContext === 0
        ? initialLimit
        : (dataFromContext + 1) * initialLimit;
    return limitToFetch;
  }, []);
  const navigate = useNavigate();

  useEffect(() => {
    dispatch(fetchUniqueDrugname());
    dispatch(fetchUniqueFormName());
  }, []);

  const search = () => {
    dispatch(searchNycbsPharmaOrdersFromApi(searchText, finalLimitToUse));
  };

  function fetchAllOrders() {
    if (validFilters?.[0]?.["filterCount"] > 0) {
      const finalFilterDataToSend = processFilterData(validFilters);
      setFilterState(true);
      dispatch(
        fetchUniqueNycbsOrderesFromApi(finalLimitToUse, finalFilterDataToSend),
      );
    } else {
      setFilterState(false);
      dispatch(fetchUniqueNycbsOrderesFromApi(finalLimitToUse));
    }
  }
  const handleEndIndexOfTable = (endIndex: number) => {
    setEndIndex(endIndex);
    if (
      ordersData &&
      pageData?.[PaginationKeys.TotalCount] !== null &&
      endIndex < (pageData?.[PaginationKeys.TotalCount] ?? 0) - 1 &&
      endIndex === ordersData.length - 1
    ) {
      const nextBatchFetch = Math.round(ordersData.length / finalLimitToUse);
      if (nextBatchFetch > 0) {
        setBatchNumberRetreived((prevBatchNumberRetrieved) => {
          if (!prevBatchNumberRetrieved.includes(nextBatchFetch)) {
            setTableDataForContext("allCmmOrders", {
              lastBatchFetched: nextBatchFetch,
            });
            return [...prevBatchNumberRetrieved, nextBatchFetch];
          }
          return prevBatchNumberRetrieved;
        });
      }
    }
  };

  const handleSearch = (searchText: string) => {
    setSearchText(searchText);
    setInitialSearch(false);
  };

  const navigateToForm = (rowData: any) => {
    const docId = rowData["id"];
    navigate(`/pharma-pa-worklists/insurance-details/${docId}`);
  };

  const resetAndFetchOrders = () => {
    dispatch(resetSingleOrderData());
    setTableDataForContext("allCmmOrders", { lastBatchFetched: 0 });
    setBatchNumberRetreived([]);
    const isValidSearch = searchText && searchText.length >= 3;
    isValidSearch ? search() : fetchAllOrders();
  };

  const onCmmOrderDeleteSuccess = () => {
    setOpenDeleteModal(false);
    setData(null);
    if (searchText && searchText.length >= 3) {
      search();
    } else {
      fetchAllOrders();
    }
  };

  const processFilterData = (validFilters: FilterValues[]) => {
    if (!validFilters?.[0]?.["filterCount"]) return {};

    const allFilters = validFilters[0];
    const filters = Object.keys(allFilters).filter((key) => key === "values");
    const filterValues = filters.map((filter) => allFilters[filter]);

    return Object.keys(filterValues?.[0] || {})
      .map((key) => ({
        [key]: filterValues?.[0]?.[key]?.values,
      }))
      .reduce((acc, curr) => ({ ...acc, ...curr }), {});
  };

  const validFilters = useMemo(() => {
    return filters.filter((filter) => filter.name === "allCmmOrders");
  }, [filters]);

  useEffect(() => {
    if (!initialSearch) {
      setTableDataForContext("allCmmOrders", {
        lastBatchFetched: 0,
        endIndex: 0,
        startIndex: 0,
        currentActiveArrayIndex: 0,
        currentStartPage: 1,
      });
      setBatchNumberRetreived([]);

      const isValidSearch = searchText && searchText.length >= 3;
      if (isValidSearch) {
        search();
      } else {
        fetchAllOrders();
      }
    }
  }, [searchText, initialSearch]);

  useEffect(() => {
    if (batchNumberRetreived.length > 0 && filterState === false) {
      if (searchText && searchText.length > 3) {
        dispatch(
          appendUniqueNycbsSearchedOrderesFromApi(searchText, finalLimitToUse),
        );
      } else {
        dispatch(appendUniqueNycbsOrderesFromApi(finalLimitToUse));
      }
    }
  }, [JSON.stringify(batchNumberRetreived)]);

  useEffect(() => {
    if (
      validFilters?.[0]?.["filterCount"] > 0 &&
      filterForTable?.filterCount &&
      filterForTable?.filterCount > 0
    ) {
      setFilterState(true);
      const finalFilterDataToSend = processFilterData(validFilters);
      setTableDataForContext("allCmmOrders", {
        endIndex: 0,
        startIndex: 0,
        currentActiveArrayIndex: 0,
        currentStartPage: 1,
        lastBatchFetched: 0,
      });
      dispatch(
        fetchUniqueNycbsOrderesFromApi(finalLimitToUse, finalFilterDataToSend),
      );
    } else {
      !initialSearch && fetchAllOrders();
      setFilterState(false);
    }
  }, [JSON.stringify(filterForTable)]);

  useEffect(() => {
    if (filterState) {
      if (endIndex === allCmmOrdersTableData.length - 1) {
        const tableAttributes = getTableDataFromContext("allCmmOrders");
        const lastBatchFetched = tableAttributes.lastBatchFetched;
        const latestBatchToFetch = (lastBatchFetched ?? 0) + 1;
        setTableDataForContext("allCmmOrders", {
          lastBatchFetched: latestBatchToFetch,
        });
        const finalFilterDataToSend = processFilterData(validFilters);
        dispatch(
          appendUniqueNycbsOrderesFromApi(
            finalLimitToUse,
            finalFilterDataToSend,
          ),
        );
      }
    }
  }, [filterState, endIndex]);

  useEffect(() => {
    if (ordersData && !loading) {
      setAllCmmOrdersTableData(generateTableDataForCmmOrdersTable(ordersData));
    } else {
      setAllCmmOrdersTableData([]);
    }
  }, [JSON.stringify(ordersData), loading]);

  useEffect(() => {
    if (initialSearch) return;
    unsubscribeRef.current = [];
    const isValidSearch = tableAttributes?.searchedText ?? "";
    isValidSearch ? search() : fetchAllOrders();
  }, [searchText, dispatch, initialSearch]);

  useEffect(() => {
    if (openDeleteModal) {
      openModal("order-delete-confirmation");
    }
  }, [openDeleteModal]);

  const createFilterForCmmDrugName = (
    uniqueDrugnames: string[] | null | undefined,
  ) => {
    return {
      id: CmmFilterKeys.DrugName,
      label: "Drug Name",
      options: Array.isArray(uniqueDrugnames)
        ? uniqueDrugnames.map((drugname) => ({
            label: drugname,
            value: drugname,
          }))
        : [],
      type: "string" as const,
    };
  };

  const createFilterForCmmFormName = (
    uniqueFormNames: string[] | null | undefined,
  ) => {
    return {
      id: CmmFilterKeys.FormName,
      label: "Form Name",
      options: Array.isArray(uniqueFormNames)
        ? uniqueFormNames.map((formname) => ({
            label: formname,
            value: formname,
          }))
        : [],
      type: "string" as const,
    };
  };

  useEffect(() => {
    setFilterData([
      createFilterForInsuranceCardDate(),
      createFilterForStatus(),
      createFilterForFormPickedVia(),
      createFilterForCmmDrugName(uniqueDrugnames?.data),
      createFilterForCmmFormName(uniqueFormNames?.data),
    ]);
  }, [uniqueDrugnames, uniqueFormNames]);

  return (
    <>
      <CustomTable
        tableHeaders={CmmOrderTableHeader}
        tableData={allCmmOrdersTableData}
        itemsPerPage={10}
        pagesPerView={4}
        count={filterState ? allCmmOrdersTableData?.length : ordersData?.length}
        totalCount={
          filterState
            ? allCmmOrdersTableData?.length
            : pageData?.[PaginationKeys.TotalCount]
        }
        isFetching={loading}
        tableName={"allCmmOrders"}
        searchingText={handleSearch}
        endIndexOfTable={handleEndIndexOfTable}
        onReviewButtonClick={(data) => navigateToForm(data)}
        onRefreshButton={resetAndFetchOrders}
        onRowClick={(data) => navigateToForm(data)}
        isDisabled={loading}
        onDeleteIconClick={(data) => {
          setOpenDeleteModal(true);
          setData(data);
        }}
        showFilterButton={!loading && searchText.length <= 3}
        filterSectionsData={filterData}
        onFilterApplyClicked={(data) => {
          setFilterForTable(data);
          dispatch(setFilter(data));
        }}
        showInLineLoader={showInLineLoader}
        showRefreshButton={true}
      />
      {openDeleteModal && (
        <CmmOrderDeleteModal
          onClose={() => {
            setOpenDeleteModal(false);
            setData(null);
          }}
          data={data}
          onDeleteSuccess={onCmmOrderDeleteSuccess}
        />
      )}
    </>
  );
};

export default CmmOrderTable;
